/**
 * ProjectArtifactsModal -- single source of truth for "what's saved
 * in this project".
 *
 * Background
 * ----------
 * Save semantics differ per tool today:
 *   - Planner: full keyframe save (explicit button)
 *   - Exclusions: explicit "save to project" button writes GeoJSON
 *   - 8760: runs auto-persist as directories under 8760_runs/
 *   - DesignPlant + Predict (since v1.9.7.19): explicit Save buttons
 *     write JSON datasets to the canonical catalog paths
 *   - uGridNET: explicit "Save to project" inside the modal writes
 *     designed GIS layers
 *   - NewSite (until v1.9.7.20+): purely client-side
 *
 * Without a unified view, a user has to walk every tool to see what
 * the project actually contains. This modal calls the new
 * GET /projects/{name}/artifacts endpoint and renders a single
 * comprehensive summary -- every dataset slot's saved status,
 * provenance, and a one-click link to the tool that produced it.
 *
 * Reachable from:
 *   - Hub: "View saved artifacts" button next to the active-project
 *     banner
 *   - ProjectIndicator dropdown: "View artifacts" entry
 *
 * Read-only by design -- the modal shows what's saved, but each
 * tool remains the authoritative place to edit / re-save its own
 * artifact. Open links navigate to the appropriate tool.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiService } from '../services/api';

interface ProjectArtifactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Project registry name (e.g. "MTU"). When null, modal renders
   *  a "no project loaded" placeholder. */
  projectName: string | null;
}

interface ArtifactSlot {
  saved: boolean;
  saved_at?: string | null;
  saved_by?: string | null;
  source?: string | null;
  path?: string | null;
  [k: string]: unknown;
}

interface ArtifactsResponse {
  project_name: string;
  project_state: 'draft' | 'published';
  artifacts: Record<string, ArtifactSlot>;
  eight_seventy_sixty_runs: Array<{
    run_id: string;
    run_name?: string | null;
    saved_at?: string | null;
    peak_kw?: number | null;
    annual_kwh?: number | null;
    load_factor?: number | null;
    n_customers?: number | null;
    incomplete?: boolean;
  }>;
  manifest_pipeline_state: Record<string, boolean>;
}

/**
 * Per-artifact display config: which dataset slot to show, how to
 * label it, the icon / color theme, which page to open when the
 * user clicks Open, and a function to summarize the slot's
 * relevant fields into a one-line caption.
 */
interface SlotDisplay {
  key: string;
  label: string;
  icon: string;
  openPath: string;
  /** Pluck the most relevant fields out of the slot for display. */
  summary: (slot: ArtifactSlot) => string;
}

const SLOTS: SlotDisplay[] = [
  {
    key: 'connections',
    label: 'Connections',
    icon: '🏠',
    openPath: '/planner',
    summary: (s) => {
      const n = (s.count as number | undefined) ?? null;
      return n !== null ? `${n.toLocaleString()} connections imported` : 'Imported via planner';
    },
  },
  {
    // NewSite (greenfield site survey) draft state. Lives next to
    // 'connections' but is a separate slot because it represents
    // work-in-progress markers that haven't been promoted to the
    // canonical Connections layer yet. The user explicitly clicks
    // "Promote (replace|append)" in NewSite to turn the draft into
    // real connections (which then lights up the Connections row
    // above).
    key: 'connections_draft',
    label: 'Survey draft (NewSite)',
    icon: '📍',
    openPath: '/new-site',
    summary: (s) => {
      const n = (s.count as number | undefined) ?? null;
      const site = (s.site_name as string | undefined) ?? null;
      const parts: string[] = [];
      if (n !== null) parts.push(`${n.toLocaleString()} pins`);
      if (site) parts.push(`site: ${site}`);
      return parts.join(' · ') || 'In-progress survey markers';
    },
  },
  {
    key: 'exclusion_zones',
    label: 'Exclusion zones',
    icon: '🚫',
    openPath: '/exclusions',
    summary: (s) => {
      const n = (s.count as number | undefined) ?? null;
      const src = (s.source as string | undefined) ?? null;
      const parts: string[] = [];
      if (n !== null) parts.push(`${n.toLocaleString()} polygons`);
      if (src) parts.push(`source: ${src}`);
      return parts.join(' · ') || 'Drawn / imported';
    },
  },
  {
    key: 'tmy',
    label: 'TMY weather data',
    icon: '☀️',
    openPath: '/design-plant',
    summary: (s) => {
      const loc = s.location as { lat?: number; lon?: number } | undefined;
      const hours = (s.hours as number | undefined) ?? null;
      const parts: string[] = [];
      if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lon)) {
        parts.push(`${(loc.lat as number).toFixed(3)}, ${(loc.lon as number).toFixed(3)}`);
      }
      if (hours) parts.push(`${hours.toLocaleString()} hours`);
      const yr = s.year_range as string | undefined;
      if (yr) parts.push(yr);
      return parts.join(' · ') || 'Fetched from PVGIS';
    },
  },
  {
    key: 'demand_8760',
    label: '8760 demand profile',
    icon: '📊',
    openPath: '/8760',
    summary: (s) => {
      const peak = (s.peak_kw as number | undefined) ?? null;
      const annual = (s.annual_kwh as number | undefined) ?? null;
      const parts: string[] = [];
      if (peak !== null) parts.push(`peak ${peak.toFixed(1)} kW`);
      if (annual !== null) parts.push(`${(annual / 1000).toFixed(1)} MWh/yr`);
      return parts.join(' · ') || 'Monte Carlo run';
    },
  },
  {
    key: 'sizing',
    label: 'Plant sizing',
    icon: '⚡',
    openPath: '/design-plant',
    summary: (s) => {
      const pv = (s.pv_kw as number | undefined) ?? null;
      const bat = (s.battery_kwh as number | undefined) ?? null;
      const lcoe = (s.lcoe as number | undefined) ?? null;
      const parts: string[] = [];
      if (pv !== null) parts.push(`PV ${pv.toFixed(0)} kW`);
      if (bat !== null) parts.push(`battery ${bat.toFixed(0)} kWh`);
      if (lcoe !== null) parts.push(`LCOE $${lcoe.toFixed(3)}/kWh`);
      return parts.join(' · ') || 'PSO optimization';
    },
  },
  {
    key: 'gensite_perimeter',
    label: 'Gen-site perimeter',
    icon: '⬛',
    openPath: '/design-plant',
    summary: (s) => {
      const m = (s.perimeter_m as number | undefined) ?? null;
      return m !== null ? `${m.toFixed(0)} m fence` : 'Saved';
    },
  },
  {
    key: 'network_layout',
    label: 'Network design',
    icon: '🔌',
    openPath: '/planner',
    summary: (s) => {
      const poles = (s.poles as number | undefined) ?? null;
      const lines = (s.lines as number | undefined) ?? null;
      const parts: string[] = [];
      if (poles !== null) parts.push(`${poles.toLocaleString()} poles`);
      if (lines !== null) parts.push(`${lines.toLocaleString()} lines`);
      return parts.join(' · ') || 'uGridNET / planner';
    },
  },
  {
    key: 'construction',
    label: 'Construction status',
    icon: '🏗️',
    openPath: '/planner',
    summary: (s) => {
      const notes = (s.notes as string | undefined) ?? null;
      return notes || 'Tracked in planner';
    },
  },
  {
    key: 'prediction',
    label: 'Prediction',
    icon: '📈',
    openPath: '/predict',
    summary: (s) => {
      const months = (s.months_to_complete as number | undefined) ?? null;
      const completion = (s.completion_date as string | undefined) ?? null;
      const parts: string[] = [];
      if (months !== null) parts.push(`${months.toFixed(1)} months`);
      if (completion) parts.push(`done ${completion}`);
      return parts.join(' · ') || 'Deployment forecast';
    },
  },
];

const ProjectArtifactsModal: React.FC<ProjectArtifactsModalProps> = ({
  isOpen, onClose, projectName,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ArtifactsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !projectName) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const resp = await apiService.getProjectArtifacts(projectName);
        if (!cancelled) setData(resp);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, projectName]);

  if (!isOpen) return null;

  const handleOpen = (path: string) => {
    onClose();
    navigate(path);
  };

  const formatTimeAgo = (iso?: string | null): string => {
    if (!iso) return '';
    try {
      const t = new Date(iso).getTime();
      const now = Date.now();
      const diffMs = now - t;
      const mins = Math.round(diffMs / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      if (days < 30) return `${days}d ago`;
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-[720px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-emerald-50 to-blue-50">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Project artifacts</h2>
            <p className="text-xs text-gray-600">
              {projectName
                ? `Everything saved to ${projectName}`
                : 'No project loaded'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {!projectName && (
            <div className="text-center text-sm text-gray-500 py-12">
              Use the project switcher in the top bar to pick a project
              first.
            </div>
          )}

          {projectName && loading && (
            <div className="text-center text-sm text-gray-500 py-12">
              Loading saved artifacts…
            </div>
          )}

          {projectName && !loading && error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              Could not load artifacts: {error}
            </div>
          )}

          {projectName && data && !loading && (
            <>
              {/* Per-slot artifact list */}
              <div className="space-y-1">
                {SLOTS.map((slot) => {
                  const a = data.artifacts[slot.key];
                  if (!a) return null;
                  const saved = a.saved;
                  return (
                    <div
                      key={slot.key}
                      className={`rounded border p-2.5 ${
                        saved ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50/60'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-xl leading-none mt-0.5">{slot.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900">
                              {slot.label}
                            </span>
                            {saved ? (
                              <span className="text-[10px] text-emerald-700 font-medium">
                                ✓ saved {formatTimeAgo(a.saved_at)}
                              </span>
                            ) : (
                              <span className="text-[10px] text-gray-400 italic">
                                not saved
                              </span>
                            )}
                          </div>
                          {saved && (
                            <div className="text-[11px] text-gray-600 mt-0.5">
                              {slot.summary(a)}
                              {a.source && a.source !== 'undefined' && (
                                <span className="text-gray-400 ml-1">· {a.source}</span>
                              )}
                            </div>
                          )}
                          {!saved && (
                            <div className="text-[11px] text-gray-500 italic mt-0.5">
                              Open the tool to create + save this artifact.
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpen(slot.openPath)}
                          className="shrink-0 text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                        >
                          {saved ? 'Open' : 'Create'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 8760 runs section -- multi-instance, distinct from
                  the single demand_8760 slot above which only carries
                  the latest run summary */}
              <div className="rounded border border-gray-200 bg-white p-3">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">
                    📊 All 8760 runs ({data.eight_seventy_sixty_runs.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => handleOpen('/8760')}
                    className="text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                  >
                    Open 8760 tool
                  </button>
                </div>
                {data.eight_seventy_sixty_runs.length === 0 ? (
                  <div className="text-[11px] text-gray-500 italic">
                    No 8760 runs saved for this project. Open the 8760
                    tool to create one.
                  </div>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {data.eight_seventy_sixty_runs.slice(0, 50).map((run) => (
                      <div
                        key={run.run_id}
                        className="text-[11px] py-1 px-2 rounded hover:bg-gray-50 grid grid-cols-[1fr_auto_auto] gap-3 items-baseline"
                      >
                        <span className="font-mono truncate text-gray-700">
                          {run.run_name && run.run_name !== run.run_id ? (
                            <>
                              {run.run_name}
                              <span className="text-gray-400 ml-1">({run.run_id})</span>
                            </>
                          ) : (
                            run.run_id
                          )}
                          {run.incomplete && (
                            <span className="ml-1 text-amber-600 italic">incomplete</span>
                          )}
                        </span>
                        <span className="text-gray-500 whitespace-nowrap">
                          {run.peak_kw != null ? `peak ${run.peak_kw.toFixed(1)} kW` : ''}
                          {run.annual_kwh != null ? ` · ${(run.annual_kwh / 1000).toFixed(1)} MWh/yr` : ''}
                        </span>
                        <span className="text-gray-400 whitespace-nowrap">
                          {formatTimeAgo(run.saved_at)}
                        </span>
                      </div>
                    ))}
                    {data.eight_seventy_sixty_runs.length > 50 && (
                      <div className="text-[10px] text-gray-400 italic pt-1">
                        Showing first 50 of {data.eight_seventy_sixty_runs.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-2 border-t bg-gray-50 text-[10px] text-gray-500">
          Read-only summary. Each tool remains the authoritative place
          to edit + re-save its artifact.
        </div>
      </div>
    </div>
  );
};

export default ProjectArtifactsModal;
